import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import ReactDOM from 'react-dom';
import { userAPI, folderAPI, utils, uploadAPI } from '../utils/api';
import InlinePdfViewer from '../components/InlinePdfViewer';
import InlineImageViewer from '../components/InlineImageViewer';
import FilePreview from '../components/FilePreview';
import ProgressBar from '../components/ProgressBar';
import ShareLink from '../components/ShareLink';
import AdUnit from '../components/AdUnit';
import SearchIcon from '../components/SearchIcon';
import UploadIcon from '../components/UploadIcon';

function MyFilesPage() {
  // Helper function to get file type icon
  const getFileTypeIcon = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    const iconMap = {
      // Images
      'jpg': '/icons/text_color_jpg.svg',
      'jpeg': '/icons/text_color_jpeg.svg',
      'png': '/icons/text_color_png.svg',
      'gif': '/icons/text_color_gif.svg',
      'webp': '/icons/text_color_webp.svg',
      'svg': '/icons/text_color_svg.svg',
      'ico': '/icons/text_color_ico.svg',
      'tiff': '/icons/text_color_tiff.svg',
      
      // Documents
      'pdf': '/icons/text_color_pdf.svg',
      'doc': '/icons/text_color_doc.svg',
      'txt': '/icons/text_color_txt.svg',
      'xls': '/icons/text_color_xls.svg',
      'ppt': '/icons/text_color_ppt.svg',
      
      // Archives
      'zip': '/icons/text_color_zip.svg',
      'rar': '/icons/text_color_rar.svg',
      'dmg': '/icons/text_color_dmg.svg',
      
      // Media
      'mp3': '/icons/text_color_mp3.svg',
      'mp4': '/icons/text_color_mp4.svg',
      'avi': '/icons/text_color_avi.svg',
      'mov': '/icons/text_color_mov.svg',
      'mpg': '/icons/text_color_mpg.svg',
      'wav': '/icons/text_color_wav.svg',
      
      // Code/Web
      'html': '/icons/text_color_html.svg',
      'css': '/icons/text_color_css.svg',
      'js': '/icons/text_color_js.svg',
      'json': '/icons/text_color_json.svg',
      'java': '/icons/text_color_java.svg',
      'csv': '/icons/text_color_csv.svg',
      
      // Design/Graphics
      'ai': '/icons/text_color_ai.svg',
      'psd': '/icons/text_color_psd.svg',
      'fig': '/icons/text_color_fig.svg',
      'blend': '/icons/text_color_blend.svg',
      'c4d': '/icons/text_color_c4d.svg',
      'cdr': '/icons/text_color_cdr.svg',
      'aep': '/icons/text_color_aep.svg',
      'skt': '/icons/text_color_skt.svg',
      
      // Executables
      'exe': '/icons/text_color_exe.svg'
    };
    
    return iconMap[ext] || '/icons/text_color_txt.svg'; // Default to txt icon
  };

  // Core state
  const [currentPath, setCurrentPath] = useState([{ id: 'root', name: 'root' }]);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [showContextMenu, setShowContextMenu] = useState(null);
  const [showViewSwitcher, setShowViewSwitcher] = useState(false);
  const [showShareModal, setShowShareModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showSidebar, setShowSidebar] = useState(false);
  const [activePreview, setActivePreview] = useState(null);
  const [contextMenuItem, setContextMenuItem] = useState(null);
  const [contextMenuPos, setContextMenuPos] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrTitle, setQrTitle] = useState('');
  const [qrLink, setQrLink] = useState('');
  
  // Missing state variables
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [deletedCount, setDeletedCount] = useState(0);
  const [showTakedownModal, setShowTakedownModal] = useState(false);
  const [takedownFolderInfo, setTakedownFolderInfo] = useState(null);
  
  // Upload state
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [totalProgress, setTotalProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState('24h'); // Default to 24 hours
  const [selectedExpiryDays, setSelectedExpiryDays] = useState(5); // Legacy compatibility
  const [isUploading2, setIsUploading2] = useState(false);
  const lastBytesByFileRef = useRef(new Map());
  const uploadStartRef = useRef(0);
  const totalBytesRef = useRef(0);

  // Subscription tier (to control expiry options)
  const [subscriptionTier, setSubscriptionTier] = useState(null);

  // Fetch subscription tier once for the current user
  useEffect(() => {
    (async () => {
      try {
        const userId = localStorage.getItem('fileshare_user_id');
        if (!userId) return;
        const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
        const res = await fetch(`${apiBase}/api/user/profile?userId=${userId}`);
        if (!res.ok) return;
        const json = await res.json();
        const tier = json?.profile?.subscriptionTier || null;
        setSubscriptionTier(tier);
      } catch (_) {}
    })();
  }, []);

  // Calculate dynamic expiry options based on total file size
  const getExpiryOptions = () => {
    // Premium users: no expiry (files stay until subscription ends)
    if (subscriptionTier && subscriptionTier.tier && subscriptionTier.tier !== 'free') {
      return [
        { value: '-1', label: 'No expiry (Premium)' }
      ];
    }
    const totalSize = pendingFiles.reduce((sum, file) => sum + file.size, 0);
    const sizeMB = totalSize / (1024 * 1024);
    // For files ≤ 100MB: 12hrs, 1, 2, 3, 5, 10, 15 days
    if (sizeMB <= 100) {
      return [
        { value: '12h', label: '12 hours' },
        { value: '24h', label: '1 day' },
        { value: '48h', label: '2 days' },
        { value: '72h', label: '3 days' },
        { value: '120h', label: '5 days' },
        { value: '240h', label: '10 days' },
        { value: '360h', label: '15 days (max)' }
      ];
    }
    // Files > 100MB (up to 10GB): 12hrs, 1, 2, 3 days (max 3 days)
    return [
      { value: '12h', label: '12 hours' },
      { value: '24h', label: '1 day' },
      { value: '48h', label: '2 days' },
      { value: '72h', label: '3 days (max)' }
    ];
  };

  // Ensure selected expiry is valid for current file size - check whenever pendingFiles changes
  React.useEffect(() => {
    if (pendingFiles.length > 0) {
      const currentOptions = getExpiryOptions();
      const validExpiryValues = currentOptions.map(opt => opt.value);
      if (!validExpiryValues.includes(selectedExpiry)) {
        // Reset to the maximum allowed value for current file size
        const maxValue = validExpiryValues[validExpiryValues.length - 1] || '24h';
        setSelectedExpiry(maxValue);
      }
    }
  }, [pendingFiles, subscriptionTier]);

  // Keep legacy selectedExpiryDays in sync with selectedExpiry
  useEffect(() => {
    const hours = parseInt(String(selectedExpiry).replace(/[^0-9]/g, '')) || 24;
    setSelectedExpiryDays(Math.max(1, Math.round(hours / 24)));
  }, [selectedExpiry]);

  // Refs
  const fileInputRef = useRef(null);
  const [folderMode, setFolderMode] = useState(false); // Track if we're in folder mode
  const isProcessingFiles = useRef(false);
  const listContainerRef = useRef(null);
  const [measuredRowHeight, setMeasuredRowHeight] = useState(64);

  // Reset page when search changes
  useEffect(() => {
    // Debounce the page reset to prevent rapid state changes
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);
  
  // Pagination
  const [itemsPerPage] = useState(5);
  const ROW_HEIGHT_PX = 64; // calibrated row height for mobile spacing
  
  // Measure actual row height in mobile to make blank space precise
  // NOTE: Runs after items are computed (effect is placed after displayItems definition)
  
  // File upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [currentFileName, setCurrentFileName] = useState('');
  const [totalFiles, setTotalFiles] = useState(0);
  const [bytesUploaded, setBytesUploaded] = useState(0);
  const [uploadResult, setUploadResult] = useState(null); // Add upload result state
  const [totalBytes, setTotalBytes] = useState(0);
  const [files, setFiles] = useState([]);
  const [processingProgress, setProcessingProgress] = useState(0); // Processing progress
  const [processedFiles, setProcessedFiles] = useState(0); // Files processed count

  // Download state
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());
  const [downloadProgress, setDownloadProgress] = useState({});
  const [showDownloadProgress, setShowDownloadProgress] = useState(false);

  // Preview functions
  const openInlinePreview = (file) => {
    console.log('Opening inline preview for file:', file);
    setActivePreview(file);
  };

  const closeInlinePreview = () => {
    setActivePreview(null);
  };

  // Check if file type is previewable
  const isPreviewable = (file) => {
    const mimeType = file.mimeType || file.contentType || file.mime_type || '';
    const filename = (file.name || file.originalFilename || '').toLowerCase();
    
    return (
      // Images
      mimeType.startsWith('image/') ||
      filename.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/) ||
      // PDFs
      mimeType === 'application/pdf' || 
      filename.endsWith('.pdf') ||
      // Text files
      mimeType.startsWith('text/') ||
      mimeType === 'application/json' ||
      mimeType === 'application/javascript' ||
      mimeType === 'application/xml' ||
      filename.match(/\.(txt|md|json|csv|log|ini|conf|yaml|yml|js|css|html|xml|py|java|cpp|c|h|sh|bat)$/)
    );
  };

  useEffect(() => {
    // Batch state updates to prevent ResizeObserver issues on mobile
    const timeoutId = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when path changes
    loadCurrentDirectory();
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [currentPath]); // Fixed ESLint warning by removing loadCurrentDirectory dependency

  // Security: Ensure this page is never indexed by search engines
  useEffect(() => {
    // Set noindex meta tags dynamically
    const setMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.getElementsByTagName('head')[0].appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Block all search engines and crawlers
    setMetaTag('robots', 'noindex, nofollow, noarchive, nosnippet, noimageindex');
    setMetaTag('googlebot', 'noindex, nofollow, noarchive, nosnippet, noimageindex');
    setMetaTag('bingbot', 'noindex, nofollow, noarchive, nosnippet');
    setMetaTag('x-robots-tag', 'noindex, nofollow, noarchive, nosnippet, noimageindex');

    // Set document title for uploads page
    document.title = 'uploads - largetransfer.net';

    // Suppress ResizeObserver errors (common on mobile)
    const originalError = console.error;
    const resizeObserverErrorHandler = (error) => {
      if (error.message && error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        // Suppress this specific error as it's harmless but annoying
        return;
      }
      originalError.apply(console, arguments);
    };
    
    console.error = resizeObserverErrorHandler;
    
    // Also handle it via window error handler
    const handleResizeObserverError = (event) => {
      if (event.message && event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        event.preventDefault();
        return true;
      }
    };
    
    window.addEventListener('error', handleResizeObserverError);

    // Cleanup function to remove sensitive data from browser history
    return () => {
      // Restore original console.error
      console.error = originalError;
      window.removeEventListener('error', handleResizeObserverError);
      
      // Clear any sensitive data from window history state
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, document.title, window.location.pathname);
      }
    };
  }, []);

  const loadCurrentDirectory = async () => {
    try {
      setLoading(true);
      setError(null);

      const currentFolderId = currentPath[currentPath.length - 1].id;
      
      if (currentFolderId === 'root') {
        // Load user's root folders
        const response = await userAPI.getUserFolders();
        const folders = response.folders || [];
        setItems(folders.map(folder => {
          // Calculate folder expiry based on the earliest expiring file
          let folderExpiryDate = null;
          if (folder.files && folder.files.length > 0) {
            const fileExpiries = folder.files
              .map(file => file.expiry_date || file.expiryDate)
              .filter(date => date)
              .map(date => new Date(date));
            
            if (fileExpiries.length > 0) {
              folderExpiryDate = new Date(Math.min(...fileExpiries)).toISOString();
            }
          }
          
          // Check if folder is taken down for copyright violation
          const folderName = folder.folder_name || folder.folderName || folder.folderId;
          const isTakenDown = folderName.startsWith('DELETED_COPYRIGHT_VIOLATION');
          let displayName = folderName;
          let takedownReason = null;
          
          if (isTakenDown) {
            // Extract original name and reason from "DELETED_COPYRIGHT_VIOLATION|reason"
            takedownReason = folderName.includes('|') ? folderName.split('|')[1] : 'Copyright violation';
            displayName = folder.original_folder_name || 'Removed Content';
          }
          
          return {
          id: folder.folderId,
          name: displayName,
          originalName: folderName,
          type: 'folder',
          size: 0,
          createdAt: folder.createdAt,
            expiryDate: folderExpiryDate,
          isPublic: true,
          files: folder.files || [],
          isTakenDown: isTakenDown,
          takedownReason: takedownReason
          };
        }));
      } else {
        // Load folder contents using OPTIMIZED D1 endpoint for 20x faster loading
        const response = await folderAPI.getFolderFilesOptimized(currentFolderId, 1, 500); // Increased from 100 to 500
        console.log('Optimized folder response:', response);
        
        // Handle files from the optimized response structure
        const files = response.files || [];
        
        setItems(files.map(file => ({
          id: file.file_id || file.fileId,
          name: file.original_filename || file.filename || file.name,
          type: 'file',
          size: file.file_size || file.size || 0,
          createdAt: file.upload_date || file.uploadedAt || file.createdAt,
          expiryDate: file.expiry_date || file.expiryDate,
          mimeType: file.mime_type || file.mimeType,
          isPublic: true,
          // Add properties needed for FilePreview component
          fileId: file.file_id || file.fileId,
          originalFilename: file.original_filename || file.filename || file.name,
          contentType: file.mime_type || file.mimeType,
          folderId: currentFolderId // Set the current folder ID for preview
        })));
      }
    } catch (err) {
      console.error('Error loading directory:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Retry refresh helper: re-fetch current folder until a given fileId is visible
  const refreshUntilVisible = async (expectedFileId, maxAttempts = 5, delayMs = 400) => {
    try {
      const currentFolderId = currentPath[currentPath.length - 1].id;
      if (currentFolderId === 'root' || !expectedFileId) return;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const response = await folderAPI.getFolderFilesOptimized(currentFolderId, 1, 100);
        const files = response.files || [];
        const found = files.some(f => (f.file_id || f.fileId) === expectedFileId);
        if (found) {
          setItems(files.map(file => ({
            id: file.file_id || file.fileId,
            name: file.original_filename || file.filename || file.name,
            type: 'file',
            size: file.file_size || file.size || 0,
            createdAt: file.upload_date || file.uploadedAt || file.createdAt,
            expiryDate: file.expiry_date || file.expiryDate,
            mimeType: file.mime_type || file.mimeType,
            isPublic: true,
            fileId: file.file_id || file.fileId,
            originalFilename: file.original_filename || file.filename || file.name,
            contentType: file.mime_type || file.mimeType,
            folderId: currentFolderId
          })));
          return;
        }
        await new Promise(r => setTimeout(r, delayMs));
      }
    } catch (e) {
      console.warn('refreshUntilVisible error:', e);
    }
  };

  // Navigation functions
  const navigateToFolder = (folderId, folderName) => {
    setCurrentPath([...currentPath, { id: folderId, name: folderName }]);
    setSelectedItems(new Set());
  };

  const navigateUp = (index) => {
    setCurrentPath(currentPath.slice(0, index + 1));
    setSelectedItems(new Set());
  };

  // Simple file selection handler
  const handleFileSelection = (files) => {
    console.log(`📁 handleFileSelection: ${files.length} files`);
    
    // Prevent double triggers
    if (isProcessingFiles.current || showExpiryModal) {
      console.log('📁 Already processing, ignoring');
      return;
    }
    
    // Check total file size (100GB limit)
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const maxTotalSize = 100 * 1024 * 1024 * 1024; // 100GB
    
    if (totalSize > maxTotalSize) {
              const totalSizeGB = (totalSize / 1024 / 1024 / 1024).toFixed(2);
        alert(`Total files size (${totalSizeGB}GB) exceeds the 100GB limit. Please select fewer or smaller files.`);
      return;
    }
    
    // Set processing flag
    isProcessingFiles.current = true;
    
    // Reset flag after delay
    setTimeout(() => {
      isProcessingFiles.current = false;
    }, 1000);
    
    // Set files and show modal
    setPendingFiles(files);
    setShowExpiryModal(true);
    console.log('📁 Files set, modal shown');
  };

  // NEW: Simple folder picker function
  const openFolderPicker = () => {
    console.log('📁 Opening folder picker');
    if (fileInputRef.current) {
      // Enable folder mode
      fileInputRef.current.setAttribute('webkitdirectory', '');
      fileInputRef.current.removeAttribute('multiple');
      setFolderMode(true);
      fileInputRef.current.click();
    }
  };

  // NEW: Simple file picker function  
  const openFilePicker = () => {
    console.log('📄 Opening file picker');
    if (fileInputRef.current) {
      // Enable file mode
      fileInputRef.current.removeAttribute('webkitdirectory');
      fileInputRef.current.setAttribute('multiple', '');
      setFolderMode(false);
      fileInputRef.current.click();
    }
  };

  // Download with progress tracking
  const downloadWithProgress = (url, filename, fileSize = 0) => {
    return new Promise((resolve, reject) => {
      console.log(`📊 FORCED Download progress tracking for: ${filename}`);
      
      // Show progress instantly
      setDownloadProgress(prev => ({
        ...prev,
        [filename]: {
          progress: 1,
          loaded: 0,
          total: fileSize || 1000000, // Default 1MB if unknown
          speed: 0,
          remainingTime: 0,
          filename: filename
        }
      }));
      
      const xhr = new XMLHttpRequest();
      
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      
      const startTime = Date.now();
      let lastLoaded = 0;
      let lastTime = startTime;
      let hasProgress = false;
      
      // Gentle optimistic ramp for servers that don't emit progress
      const progressInterval = setInterval(() => {
        if (!hasProgress) {
          setDownloadProgress(prev => ({
            ...prev,
            [filename]: {
              progress: Math.min((prev[filename]?.progress || 1) + 20, 90),
              loaded: prev[filename]?.loaded || 0,
              total: fileSize || 1000000,
              speed: prev[filename]?.speed || 0,
              remainingTime: prev[filename]?.remainingTime || 0,
              filename: filename
            }
          }));
        }
      }, 150); // snappier UI
      
      xhr.onprogress = (event) => {
        hasProgress = true;
        clearInterval(progressInterval);
        console.log(`📊 REAL Download progress: ${event.loaded}/${event.total} bytes`);
        
        if (event.lengthComputable) {
          const now = Date.now();
          const timeDiff = (now - lastTime) / 1000; // seconds
          const bytesDiff = event.loaded - lastLoaded;
          
          if (timeDiff > 0.1) { // Update every 100ms
            const speed = bytesDiff / timeDiff; // bytes per second
            const remainingBytes = event.total - event.loaded;
            const remainingTime = speed > 0 ? remainingBytes / speed : 0;
            
            setDownloadProgress(prev => ({
              ...prev,
              [filename]: {
                progress: (event.loaded / event.total) * 100,
                loaded: event.loaded,
                total: event.total,
                speed: speed,
                remainingTime: remainingTime,
                filename: filename
              }
            }));
            
            lastLoaded = event.loaded;
            lastTime = now;
          }
        }
      };
      
      xhr.onload = () => {
        clearInterval(progressInterval);
        console.log(`✅ FORCED Download completed: ${filename}, status: ${xhr.status}`);
        
        if (xhr.status === 200) {
          // Show 100% progress
          setDownloadProgress(prev => ({
            ...prev,
            [filename]: {
              progress: 100,
              loaded: xhr.response.size,
              total: xhr.response.size,
              speed: 0,
              remainingTime: 0,
              filename: filename
            }
          }));
          
          const blob = xhr.response;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          window.URL.revokeObjectURL(url);
          
          // Remove from progress tracking after small delay
          setTimeout(() => {
            setDownloadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[filename];
              return newProgress;
            });
          }, 2000); // Show complete for 2 seconds
          
          resolve();
        } else {
          reject(new Error(`Download failed: ${xhr.status}`));
        }
      };
      
      xhr.onerror = () => {
        clearInterval(progressInterval);
        console.error(`❌ FORCED Download error for: ${filename}`);
        setDownloadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[filename];
          return newProgress;
        });
        reject(new Error('Network error during download'));
      };
      
      xhr.send();
    });
  };

  const handleFileUpload = async (selectedFiles, expiryDays = 5) => {
    const currentFolderId = currentPath[currentPath.length - 1].id;
    
    setUploading(true);
    setUploadResult(null); // Clear previous upload result
    setFiles(selectedFiles);
    setTotalFiles(selectedFiles.length);
    setCurrentFileIndex(0);
    setUploadProgress(0);
    setBytesUploaded(0);
    setTotalBytes(selectedFiles.reduce((total, file) => total + file.size, 0));
    setProcessingProgress(0); // Reset processing progress
    setProcessedFiles(0); // Reset processed files count

    try {
      console.log(`🚀 OPTIMIZED: Starting batch upload for ${selectedFiles.length} files`);
      
      // OPTIMIZED: Upload all files in a single batch instead of one by one
        const uploadOptions = {
          expiryDays: expiryDays
        };
        
        // If uploading to an existing folder (not root), pass folder and user info
        if (currentFolderId !== 'root') {
          uploadOptions.folderId = currentFolderId;
          uploadOptions.userId = utils.getCurrentUserId();
        console.log(`📁 Uploading to existing folder: ${currentFolderId}`);
        } else {
        console.log('📁 Creating new folder for batch upload');
      }

      // Initialize batch upload for all files at once
      const initResponse = await uploadAPI.initializeUpload(selectedFiles, uploadOptions);
      console.log('✅ Batch upload initialized:', initResponse);
      
      const { uploadUrls, folderId } = initResponse;
      
      // ENTERPRISE: Show share link IMMEDIATELY after upload completion (like SendGB)
      console.log('🚀 INSTANT: All files uploaded to R2 - showing share link immediately');
      
      // Set progress to show upload is starting
      setUploadProgress(5);
      uploadStartRef.current = Date.now();
      lastBytesByFileRef.current = new Map();
      totalBytesRef.current = selectedFiles.reduce((s, f) => s + (f?.size || 0), 0);
      
        if (!uploadUrls || !Array.isArray(uploadUrls) || uploadUrls.length === 0) {
          throw new Error('Invalid upload response: missing upload URLs');
        }

      // ENTERPRISE BATCH UPLOAD: Upload files in controlled batches to avoid browser limits
      const BATCH_SIZE = 6; // Browser connection limit (6-8 concurrent connections)
      const results = [];
      
      console.log(`🚀 ENTERPRISE: Uploading ${selectedFiles.length} files in batches of ${BATCH_SIZE}`);
      
      const MAX_RETRIES = 3;
      const baseDelayMs = 800;
      function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }
      function backoffDelay(attempt) { return baseDelayMs * Math.pow(2, attempt); }
      
      for (let batchStart = 0; batchStart < uploadUrls.length; batchStart += BATCH_SIZE) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, uploadUrls.length);
        const batch = uploadUrls.slice(batchStart, batchEnd);
        
        console.log(`📦 Processing batch ${Math.floor(batchStart / BATCH_SIZE) + 1}: files ${batchStart + 1}-${batchEnd}`);
        
        // Upload this batch in parallel with per-file retries
        const batchPromises = batch.map(async (uploadItem, batchIndex) => {
          const globalIndex = batchStart + batchIndex;
          const file = selectedFiles[globalIndex];
        const uploadUrl = uploadItem.uploadUrl;
        const fileId = uploadItem.fileId;

          console.log(`📤 Uploading file ${globalIndex + 1}/${selectedFiles.length}: ${file.name}`);

          for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
        await uploadAPI.uploadToR2Smart(file, uploadUrl, fileId, uploadItem.r2Key, (progress) => {
                // Convert this file progress to absolute bytes and compute delta
                const currentBytes = Math.max(0, Math.min(file.size, (progress / 100) * file.size));
                const lastBytes = lastBytesByFileRef.current.get(fileId) || 0;
                const delta = Math.max(0, currentBytes - lastBytes);
                if (delta > 0) {
                  lastBytesByFileRef.current.set(fileId, currentBytes);
                  const elapsed = Math.max(0.001, (Date.now() - uploadStartRef.current) / 1000);
                  setBytesUploaded(prev => {
                    const capped = Math.min(prev + delta, totalBytesRef.current || prev + delta);
                    setUploadSpeed(capped / elapsed);
                    return capped;
                  });
                }
                // Progress by share of total size keeps UI smooth
                const totalSize = selectedFiles.reduce((total, f) => total + f.size, 0);
                const fileProgress = (currentBytes / totalSize) * 100;
                setUploadProgress(prev => Math.min(99, Math.max(0, prev + fileProgress - ((lastBytes / totalSize) * 100))));
              });
              console.log(`✅ File ${globalIndex + 1} uploaded successfully: ${file.name}`);
              return { success: true, fileId, fileName: file.name, uploaded: true };
            } catch (err) {
              const willRetry = attempt < MAX_RETRIES;
              console.warn(`🔁 Upload failed (attempt ${attempt + 1}/${MAX_RETRIES + 1}) for ${file.name}: ${err?.message}`);
              if (!willRetry) {
                return { success: false, error: err?.message || 'upload failed', fileName: file.name };
              }
              const delay = backoffDelay(attempt);
              console.log(`⏳ Backing off ${delay}ms before retrying ${file.name}...`);
              await sleep(delay);
            }
          }
        });
        
        // Wait for this batch to complete
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
        
        // Small delay between batches to prevent overwhelming the server
        if (batchEnd < uploadUrls.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // Check results
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);
      
      console.log(`📊 Upload complete: ${successful.length} successful, ${failed.length} failed`);
      
      // ENTERPRISE: Only show success if ALL files uploaded successfully
      if (failed.length > 0) {
        console.error(`❌ ENTERPRISE ERROR: ${failed.length} files failed to upload out of ${selectedFiles.length}`);
        console.error('Failed files:', failed);
        
        // Show error to user
        setError(`${failed.length} out of ${selectedFiles.length} files failed to upload. Please try again.`);
        setUploading(false);
        return;
      }
      
      // Only proceed if ALL files uploaded successfully
      console.log('✅ ENTERPRISE: All files uploaded successfully to R2');
      
      // INSTANT SHARE LINK: Show immediately after R2 upload (like SendGB)
      setUploadProgress(100);
      setProcessingProgress(100); // Processing complete
      
      // Calculate total size for share link
      const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
      
      // NO popup for MyFilesPage uploads - only show popup on HomePage
      // Files will appear in the list automatically after refresh
      
      console.log('✅ INSTANT: Share link shown immediately after R2 upload - processing continues in background');
      
      // Start background processing (fire-and-forget)
      console.log('🔄 Background processing started for verification and metadata...');
      successful.forEach(async (result, index) => {
        try {
          if (result.success) {
            // Fire-and-forget verification and completion
            await uploadAPI.completeUploadAsync(result.fileId, true, {
              folderId: currentFolderId !== 'root' ? currentFolderId : folderId,
              userId: utils.getCurrentUserId(),
              r2Key: uploadUrls[index].r2Key,
              filename: selectedFiles[index].name,
              fileSize: selectedFiles[index].size,
              contentType: selectedFiles[index].type,
              expiryDate: initResponse?.expiryDate
            });
          }
        } catch (error) {
          console.warn(`⚠️ Background processing failed for file ${index + 1}:`, error);
        }
      });
      
      // Refresh the directory to show new files (processing will complete shortly)
      setTimeout(async () => {
        setLoading(true);
        await loadCurrentDirectory();
        setLoading(false);
        console.log('📁 Directory refreshed successfully');
      }, 1000); // Reduced delay to 1 second
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      // Reset upload state (but keep uploadResult for share link)
      setUploading(false);
      setUploadProgress(0);
      setCurrentFileIndex(0);
      setCurrentFileName('');
      setTotalFiles(0);
      setBytesUploaded(0);
      setTotalBytes(0);
      setFiles([]);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const currentFolderId = currentPath[currentPath.length - 1].id;
      const parentFolderId = currentFolderId === 'root' ? null : currentFolderId;
      
      console.log(`Creating folder "${newFolderName}" in parent: ${parentFolderId || 'root'}`);
      
      await folderAPI.createFolder(newFolderName.trim(), parentFolderId);
      
      setShowCreateFolder(false);
      setNewFolderName('');
      
      // Add delay to ensure backend processing, then refresh
      setTimeout(async () => {
        // Force a complete reload of the directory with cache bust
        setLoading(true);
        await loadCurrentDirectory();
        setLoading(false);
        console.log('Folder created successfully and directory refreshed');
      }, 1500); // Increased delay to 1.5 seconds
    } catch (error) {
      console.error('Failed to create folder:', error);
      alert('Failed to create folder: ' + error.message);
    }
  };

  const handleDownload = async (file) => {
    const fileId = file.id || file.fileId || file.file_id;
    
    try {
      const filename = file.name || file.originalFilename || file.original_filename || `file_${fileId}`;
      
      if (!fileId) {
        alert('File ID not found');
        return;
      }

      // Set loading state for this specific file
      setDownloadingFiles(prev => new Set([...prev, fileId]));
      setShowDownloadProgress(true);
      
      // For files, we need the folder ID from the current path
      let downloadFolderId;
      if (currentPath.length === 0) {
        // We're in root, this shouldn't happen for files
        alert('Cannot download file: folder not found');
        return;
      } else {
        // Get folder ID from current path
        downloadFolderId = currentPath[currentPath.length - 1].id;
      }
      
      console.log(`🔄 Starting download for file: ${filename} (ID: ${fileId})`);
      
      // Use regular download endpoint (now serves files directly)
      const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
      
      // Get current user ID for self-download detection
      const currentUserId = localStorage.getItem('fileshare_user_id') || null;
      const apiUrl = currentUserId 
        ? `${apiBase}/api/download/${downloadFolderId}/${fileId}?userId=${encodeURIComponent(currentUserId)}`
        : `${apiBase}/api/download/${downloadFolderId}/${fileId}`;
      
      const res = await fetch(apiUrl);
      if (!res.ok) {
        if (res.status === 403) {
          const errorData = await res.json();
          throw new Error(errorData.message || 'Bandwidth limit exceeded');
        }
        throw new Error(`Download failed: ${res.status}`);
      }
      
      // Get the file blob from the response (direct file serving)
      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      
      // Use reliable download method
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
      console.log(` Download triggered: ${filename}`);
      
    } catch (error) {
      console.error('❌ Download failed:', error);
      
      // Provide specific error messages based on error type
      let userMessage = 'Download failed. Please try again.';
      if (error.message.includes('Failed to fetch')) {
        userMessage = 'Network error: Unable to reach download servers. Please check your internet connection and try again.';
      } else if (error.message.includes('CORS')) {
        userMessage = 'Browser security error: Try refreshing the page and downloading again.';
      } else if (error.message.includes('404')) {
        userMessage = 'File not found: This file may have been deleted or expired.';
      } else if (error.message.includes('403')) {
        userMessage = 'Access denied: You do not have permission to download this file.';
      }
      
      // Suppress alert popup; log instead
      console.warn(`${userMessage} | details: ${error.message}`);
    } finally {
      // Remove loading state for this file
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        
        // Hide download progress if no more downloads
        if (newSet.size === 0) {
          setShowDownloadProgress(false);
          setDownloadProgress({});
        }
        
        return newSet;
      });
    }
  };

  // QR Code helpers
  const openQRForItem = async (item) => {
    try {
      let link = '';
      let title = '';
      if (item.type === 'folder') {
        // For folders, always show the public folder link
        link = `${window.location.origin}/d/${item.id}`;
        title = item.name || item.id;
      } else {
        // For files, also show the parent folder link (not direct worker URL)
        const currentFolderId = currentPath[currentPath.length - 1].id;
        link = `${window.location.origin}/d/${currentFolderId}`;
        title = item.name || item.originalFilename || item.original_filename || item.filename || 'File';
      }

      setQrLink(link);
      setQrTitle(title);
      const dataUrl = await QRCode.toDataURL(link, {
        width: 320,
        margin: 2,
        color: { dark: '#1f2937', light: '#ffffff' }
      });
      setQrDataUrl(dataUrl);
      setShowQRModal(true);
    } catch (e) {
      console.error('Failed to generate QR code:', e);
      alert('Failed to generate QR code');
    }
  };



  const handleRename = async (itemId, newName) => {
    if (!newName || !newName.trim()) {
      alert('Please enter a valid name');
      return;
    }

    try {
      console.log('Renaming item:', { itemId, newName });
      
      // Find the item to determine if it's a folder or file
      const item = items.find(i => i.id === itemId);
      
      if (item && item.type === 'folder') {
        // Rename folder using the API
        await userAPI.renameFolder(itemId, newName.trim());
        console.log('Folder renamed successfully');
        setSuccessMessage('Folder renamed successfully.');
        setShowDeleteSuccess(true);
        setDeletedCount(1);
      } else if (item && item.type === 'file') {
        await userAPI.renameFile(itemId, newName.trim());
        console.log('File renamed successfully');
        setSuccessMessage('File renamed successfully.');
        setShowDeleteSuccess(true);
        setDeletedCount(1);
      } else {
        // Fallback: attempt file rename when item lookup fails
        await userAPI.renameFile(itemId, newName.trim());
        console.log('File renamed successfully (fallback path)');
        setSuccessMessage('File renamed successfully.');
        setShowDeleteSuccess(true);
      }
      
      // Refresh the directory to show updated name
      await loadCurrentDirectory();
      
    } catch (error) {
      console.error('Rename failed:', error);
      alert('Rename failed: ' + error.message);
    }
  };

  const handleDelete = (itemIds) => {
    console.log('🗑️ DELETE: handleDelete called with itemIds:', itemIds);
    setItemsToDelete(itemIds);
    setShowDeleteConfirm(true);
    console.log('🗑️ DELETE: Modal should be shown now');
  };

  const confirmDelete = async () => {
    console.log('🗑️ DELETE: confirmDelete started');
    setShowDeleteConfirm(false);
    
    console.log('🗑️ DELETE: Items to delete:', itemsToDelete);
    console.log('🗑️ DELETE: Items to delete type:', typeof itemsToDelete, Array.isArray(itemsToDelete));
    console.log('🗑️ DELETE: Current items array:', items.length, 'items');
    console.log('🗑️ DELETE: User agent:', navigator.userAgent);
    console.log('🗑️ DELETE: Screen size:', window.screen.width, 'x', window.screen.height);
    
    if (!itemsToDelete || itemsToDelete.length === 0) {
      console.error('🗑️ DELETE: No items to delete!');
      alert('No items selected for deletion');
      return;
    }
    
    const currentFolderId = currentPath[currentPath.length - 1].id;
    console.log('🗑️ DELETE: Current folder ID:', currentFolderId);
    
    const deletionResults = [];
    
    // Process each deletion individually
    for (const itemId of itemsToDelete) {
      try {
        console.log(`🗑️ DELETE: Processing item ${itemId}`);
        
        // Find the item to determine if it's a folder or file
        const item = items.find(i => i.id === itemId);
        console.log(`🗑️ DELETE: Found item:`, item);
        
        if (!item) {
          console.error(`🗑️ DELETE: Item ${itemId} not found in items array`);
          console.error(`🗑️ DELETE: Available items:`, items.map(i => ({ id: i.id, name: i.name, type: i.type })));
          console.error(`🗑️ DELETE: Looking for ID:`, itemId, typeof itemId);
          
          // Try to delete anyway - maybe it exists on the server but not in local cache
          try {
            console.log(`🗑️ DELETE: Attempting to delete unknown item ${itemId} as file first...`);
            await folderAPI.deleteFile(currentFolderId, itemId);
            console.log(`🗑️ DELETE: Successfully deleted unknown item ${itemId} as file`);
            deletionResults.push({ 
              itemId, 
              name: `Unknown item (${itemId})`, 
              type: 'file',
              success: true 
            });
          } catch (fileError) {
            console.log(`🗑️ DELETE: Failed as file, trying as folder...`, fileError.message);
            try {
              await folderAPI.deleteFolder(itemId);
              console.log(`🗑️ DELETE: Successfully deleted unknown item ${itemId} as folder`);
              deletionResults.push({ 
                itemId, 
                name: `Unknown item (${itemId})`, 
                type: 'folder',
                success: true 
              });
            } catch (folderError) {
              console.error(`🗑️ DELETE: Failed to delete unknown item ${itemId}:`, folderError.message);
              deletionResults.push({ 
                itemId, 
                name: `Unknown item (${itemId})`, 
                type: 'unknown',
                success: false, 
                error: `Item not found locally and server deletion failed: ${folderError.message}` 
              });
            }
          }
          continue;
        }
        
        if (item && item.type === 'folder') {
          // Delete folder using the API
          console.log(`🗑️ DELETE: Starting folder deletion for: ${item.name} (${itemId})`);
          console.log(`🗑️ DELETE: Calling folderAPI.deleteFolder(${itemId})`);
          
          const deleteResult = await folderAPI.deleteFolder(itemId);
          console.log('🗑️ DELETE: Folder deleted successfully:', itemId, deleteResult);
          deletionResults.push({ itemId, name: item.name, type: 'folder', success: true });
          
        } else {
          // Delete file using the deleteFile API
          console.log(`🗑️ DELETE: Starting file deletion for: ${item.name} (${itemId})`);
          console.log(`🗑️ DELETE: Calling folderAPI.deleteFile(${currentFolderId}, ${itemId})`);
          
          const deleteResult = await folderAPI.deleteFile(currentFolderId, itemId);
          console.log('🗑️ DELETE: File deleted successfully:', itemId, deleteResult);
          deletionResults.push({ itemId, name: item.name, type: 'file', success: true });
        }
        
              } catch (error) {
          console.error(`🗑️ DELETE: Failed to delete item ${itemId}:`, error);
          console.error(`🗑️ DELETE: Error details:`, {
            message: error?.message || 'Unknown error',
            stack: error?.stack || 'No stack trace',
            name: error?.name || 'Unknown error type'
          });
          
          // Safe item lookup with fallback
          const failedItem = items.find(i => i.id === itemId);
          const itemName = failedItem?.name || `Unknown item (${itemId})`;
          const itemType = failedItem?.type || 'unknown';
          
          deletionResults.push({ 
            itemId, 
            name: itemName, 
            type: itemType,
            success: false, 
            error: error?.message || 'Unknown deletion error' 
          });
        }
    }
    
    console.log('🗑️ DELETE: Deletion results:', deletionResults);
    
    // Clear selection and refresh directory
    setSelectedItems(new Set());
    
    // Try to refresh directory, but don't fail if it errors
    try {
      console.log('🗑️ DELETE: Refreshing directory...');
      await loadCurrentDirectory();
      console.log('🗑️ DELETE: Directory refreshed successfully');
    } catch (refreshError) {
      console.warn('🗑️ DELETE: Directory refresh failed after deletion (this is normal if folder was deleted):', refreshError);
      // This is expected if we deleted the current folder we're viewing
    }
    
    // Check results and show appropriate message
    const successful = deletionResults.filter(r => r.success);
    const failed = deletionResults.filter(r => !r.success);
    
    console.log(`🗑️ DELETE: ${successful.length} successful, ${failed.length} failed`);
    
    if (failed.length === 0) {
      // All deletions successful
      console.log('🗑️ DELETE: All deletions successful, showing success modal');
      setDeletedCount(successful.length);
      setSuccessMessage(`${successful.length} content item(s) deleted successfully.`);
      setShowDeleteSuccess(true);
      
      // Auto-hide success modal after 3 seconds
      setTimeout(() => {
        setShowDeleteSuccess(false);
      }, 3000);
      
    } else if (successful.length === 0) {
      // All deletions failed
      console.error('🗑️ DELETE: All deletions failed');
      const errorDetails = failed.map(f => `• ${f.name}: ${f.error}`).join('\n');
      alert(`All deletions failed:\n\n${errorDetails}\n\nPlease check the browser console for technical details.`);
    } else {
      // Partial success
      console.warn('🗑️ DELETE: Partial deletion success');
      const successNames = successful.map(s => s.name).join(', ');
      const failedDetails = failed.map(f => `• ${f.name}: ${f.error}`).join('\n');
      alert(`Partial deletion completed:\n\n✅ Successfully deleted: ${successNames}\n\n❌ Failed to delete:\n${failedDetails}`);
    }
    
    setItemsToDelete([]);
    console.log('🗑️ DELETE: confirmDelete completed');
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemsToDelete([]);
  };

  // Bulk operations
  const handleBulkDownload = async () => {
    if (selectedItems.size === 0) return;

    try {
      // Use download progress UI, not upload state
      setShowDownloadProgress(true);
      
      // Get selected files (filter out folders)
      const selectedFiles = displayItems.filter(item => 
        selectedItems.has(item.id) && item.type === 'file'
      );
      
      if (selectedFiles.length === 0) {
        alert('No files selected for download. Only files can be downloaded.');
        return;
      }
      
      // Download files individually (mobile-friendly, no ZIP)
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        
        try {
          // Add small delay between downloads to avoid overwhelming the browser
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          await handleDownload(file);
        } catch (fileError) {
          console.error(`Failed to download ${file.name}:`, fileError);
          // Continue with other files
        }
      }

      // Clear selection after download
      setSelectedItems(new Set());
      
    } catch (err) {
      console.error('Bulk download failed:', err);
      alert(`Download failed: ${err.message}. Please try again.`);
    } finally {
      // Hide download progress shortly after
      setTimeout(() => {
        setShowDownloadProgress(false);
        setDownloadProgress({});
      }, 1200);
    }
  };

  const handleBulkDelete = () => {
    if (selectedItems.size === 0) return;

    const selectedItemsList = displayItems.filter(item => selectedItems.has(item.id));
    console.log('🗑️ BULK DELETE: Selected items for deletion:', selectedItemsList);
    setItemsToDelete(selectedItemsList.map(item => item.id)); // Convert to IDs like individual delete
    setShowDeleteConfirm(true);
  };

  const handleShare = () => {
    if (selectedItems.size === 0) return;
    
    const selectedItemsList = displayItems.filter(item => selectedItems.has(item.id));
    
    // Create share URLs for selected items
    const shareData = selectedItemsList.map(item => {
      if (item.type === 'folder') {
        return `${window.location.origin}/d/${item.id}`;
      } else {
        // For files, share the parent folder
        const currentFolderId = currentPath[currentPath.length - 1].id;
        return `${window.location.origin}/d/${currentFolderId}`;
      }
    });
    
    // Copy to clipboard or show share modal
    if (shareData.length === 1) {
      navigator.clipboard.writeText(shareData[0]).then(() => {
        alert('Share link copied to clipboard!');
      }).catch(() => {
        alert(`Share link: ${shareData[0]}`);
      });
    } else {
      const allLinks = shareData.join('\n');
      navigator.clipboard.writeText(allLinks).then(() => {
        alert('Share links copied to clipboard!');
      }).catch(() => {
        alert(`Share links:\n${allLinks}`);
      });
    }
  };

  // Handle share for single item
  const handleSingleShare = (item) => {
    let shareUrl;
    if (item.type === 'folder') {
      shareUrl = `${window.location.origin}/d/${item.id}`;
    } else {
      // For files, share the parent folder
      const currentFolderId = currentPath[currentPath.length - 1].id;
      shareUrl = `${window.location.origin}/d/${currentFolderId}`;
    }
    
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('Share link copied to clipboard!');
    }).catch(() => {
      alert(`Share link: ${shareUrl}`);
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === displayItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(displayItems.map(item => item.id)));
    }
  };

  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const toggleItemVisibility = async (item) => {
    try {
      // For demo - in real app, implement API call
      await new Promise(resolve => setTimeout(resolve, 300));
      await loadCurrentDirectory();
    } catch (error) {
      alert('Failed to update visibility: ' + error.message);
    }
  };

  // Search only (sorting removed)
  const getFilteredAndSortedItems = () => {
    let filtered = items;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // No sorting - return items in their natural order
    return filtered;
  };

  // Get paginated items
  const getPaginatedItems = () => {
    const filtered = getFilteredAndSortedItems();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      items: filtered.slice(startIndex, endIndex),
      totalItems: filtered.length,
      totalPages: Math.ceil(filtered.length / itemsPerPage)
    };
  };

  const { items: displayItems, totalItems, totalPages } = getPaginatedItems();

  // Measure actual row height in mobile to make blank space precise
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth > 768) return;
    const container = listContainerRef.current;
    if (!container) return;
    const firstRow = container.querySelector('[data-row="file-row"]');
    if (firstRow) {
      const rect = firstRow.getBoundingClientRect();
      const height = Math.round(rect.height);
      if (height && Math.abs(height - measuredRowHeight) > 1) {
        setMeasuredRowHeight(height);
      }
    }
  }, [currentPage, displayItems.length]);

  // Generate page numbers for Google-style pagination
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Maximum number of page buttons to show
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Smart pagination logic
      const sidePages = 2; // Pages to show on each side of current page
      
      if (currentPage <= 4) {
        // Show first 5 pages + ... + last page
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        if (totalPages > 6) {
          pages.push('...');
          pages.push(totalPages);
        }
      } else if (currentPage >= totalPages - 3) {
        // Show first page + ... + last 5 pages
        pages.push(1);
        if (totalPages > 6) {
          pages.push('...');
        }
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first page + ... + current-1, current, current+1 + ... + last page
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - sidePages; i <= currentPage + sidePages; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (loading) {
    return (
      <div style={{ 
        backgroundColor: '#1a1d29', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '32px', 
            height: '32px', 
            border: '2px solid #1877f2', 
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            margin: '0 auto 16px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#9ca3af', fontSize: '12px' }}>Loading your files...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        backgroundColor: '#1a1d29', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', margin: '0 auto', padding: '0 16px' }}>
          <div style={{ 
            backgroundColor: '#ef444420', 
            borderRadius: '50%', 
            width: '48px', 
            height: '48px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            margin: '0 auto 16px',
            border: '1px solid #ef444440'
          }}>
            <svg width="24" height="24" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#e2e8f0', marginBottom: '8px' }}>Error Loading Files</h2>
          <p style={{ color: '#9ca3af', marginBottom: '24px', fontSize: '12px' }}>{error}</p>
          <button 
            onClick={loadCurrentDirectory}
            style={{
              backgroundColor: '#1877f2',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '2px',
              fontSize: '12px',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#166fe5'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#1877f2'}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      backgroundColor: '#1a1d29',
      position: 'relative'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>
        
        {/* Breadcrumb Navigation */}
        <div style={{ 
          backgroundColor: '#242938', 
          border: '1px solid #374151',
          borderRadius: '2px',
          padding: '12px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {currentPath.map((pathItem, index) => (
            <React.Fragment key={pathItem.id}>
              <button
                onClick={() => navigateUp(index)}
                style={{
                  backgroundColor: index === currentPath.length - 1 ? '#1877f220' : 'transparent',
                  color: index === currentPath.length - 1 ? '#1877f2' : '#9ca3af',
                  border: 'none',
                  padding: '4px 8px',
                  borderRadius: '2px',
                  fontSize: '12px',
                  cursor: index === currentPath.length - 1 ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                disabled={index === currentPath.length - 1}
              >
                {index === 0 && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"/>
                  </svg>
                )}
                {pathItem.name}
              </button>
              {index < currentPath.length - 1 && (
                <span style={{ color: '#6b7280' }}>›</span>
              )}
            </React.Fragment>
          ))}
          
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              backgroundColor: '#1a1d29',
              padding: '4px 8px',
              borderRadius: '2px',
              fontSize: '11px',
              color: '#1877f2'
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              public
            </div>
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
              {new Date().toLocaleDateString('en-GB', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric' 
              })}, {new Date().toLocaleTimeString('en-GB', { 
                hour: '2-digit', 
                minute: '2-digit'
              })}
            </span>
          </div>
        </div>

        {/* Toolbar */}
        <div style={{
          backgroundColor: '#242938',
          border: '1px solid #374151', 
          borderRadius: '2px',
          padding: '12px 16px',
          marginBottom: '20px'
        }}>
          {/* Mobile Layout (≤768px) */}
          {window.innerWidth <= 768 ? (
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Select All (mobile) - First */}
              {displayItems.length > 0 && (
                <div style={{
                    display: 'flex', 
                    alignItems: 'center', 
                  marginBottom: '0'
                }}>
                  <button
                    onClick={handleSelectAll}
                    title={`Select All (${displayItems.length})`}
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.6)',
                      backdropFilter: 'blur(6px)',
                      color: '#ffffff',
                      border: '1px solid #334155',
                      width: '40px',
                      height: '40px',
                      borderRadius: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                    <rect x="1" y="1" width="14" height="14" rx="2" fill={selectedItems.size === displayItems.length ? '#1877f2' : 'transparent'} stroke={selectedItems.size === displayItems.length ? '#1877f2' : '#4b5563'} />
                    {selectedItems.size === displayItems.length && (
                      <path d="M4 8l3 3 5-6" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    )}
                  </svg>
                  </button>
                </div>
              )}

                              {/* Primary Actions Row */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // CRITICAL: Synchronous trigger for regular file upload
                    if (fileInputRef.current && !showExpiryModal) {
                      fileInputRef.current.removeAttribute('webkitdirectory');
                      fileInputRef.current.setAttribute('multiple', '');
                      setFolderMode(false);
                      fileInputRef.current.click();
                    }
                  }}
                  // Enable on root as well
                  style={{
                    backgroundColor: 'rgba(30, 41, 59, 0.6)',
                    backdropFilter: 'blur(6px)',
                    color: '#ffffff',
                    border: '1px solid #334155',
                    width: '40px',
                    height: '40px',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 1
                  }}
                  title="Upload Files"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M12 3v10"/>
                    <path d="M8 7l4-4 4 4"/>
                    <path d="M5 21h14a2 2 0 0 0 2-2v-3"/>
                    <path d="M3 16v3a2 2 0 0 0 2 2"/>
                  </svg>
                </button>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // CRITICAL: Synchronous trigger - no async operations allowed
                    if (fileInputRef.current && !showExpiryModal) {
                      fileInputRef.current.setAttribute('webkitdirectory', '');
                      fileInputRef.current.removeAttribute('multiple');
                      setFolderMode(true);
                      fileInputRef.current.click();
                    }
                  }}
                  // Enable on root as well
                  style={{
                    backgroundColor: 'rgba(30, 41, 59, 0.6)',
                    backdropFilter: 'blur(6px)',
                    color: '#ffffff',
                    border: '1px solid #334155',
                    width: '40px',
                    height: '40px',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 1
                  }}
                  title="Upload Folder"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M10 4H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7l-3-2z"/>
                    <path d="M12 9l4 4h-3v4h-2v-4H8l4-4z"/>
              </svg>
                </button>
              </div>


              {/* Secondary Actions Row (when items are selected) */}
              {selectedItems.size > 0 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap' }}>
                  <button 
                    onClick={handleShare}
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.6)',
                      backdropFilter: 'blur(6px)',
                      color: '#ffffff',
                      border: '1px solid #334155',
                    width: '40px',
                    height: '40px',
                    borderRadius: '3px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Share"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18,16.08C17.24,16.08 16.56,16.38 16.04,16.85L8.91,12.7C8.96,12.47 9,12.24 9,12C9,11.76 8.96,11.53 8.91,11.3L15.96,7.19C16.5,7.69 17.21,8 18,8A3,3 0 0,0 21,5A3,3 0 0,0 18,2A3,3 0 0,0 15,5C15,5.24 15.04,5.47 15.09,5.7L8.04,9.81C7.5,9.31 6.79,9 6,9A3,3 0 0,0 3,12A3,3 0 0,0 6,15C6.79,15 7.5,14.69 8.04,14.19L15.16,18.34C15.11,18.55 15.08,18.77 15.08,19C15.08,20.61 16.39,21.91 18,21.91C19.61,21.91 20.92,20.61 20.92,19A2.92,2.92 0 0,0 18,16.08Z"/>
                    </svg>
                  </button>
                  <button 
                    onClick={handleBulkDownload}
                    disabled={uploading}
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.6)',
                      backdropFilter: 'blur(6px)',
                      color: '#ffffff',
                      border: '1px solid #334155',
                      width: '40px',
                      height: '40px',
                      borderRadius: '3px',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: uploading ? 0.6 : 1
                    }}
                    title={uploading ? 'Downloading...' : `Download (${selectedItems.size})`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 3v10.17l3.59-3.58L17 11l-5 5-5-5 1.41-1.41L11 13.17V3h1z"/>
                      <path d="M5 18h14v2H5z"/>
                    </svg>
                  </button>
                  <button 
                    onClick={handleBulkDelete}
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.6)',
                      backdropFilter: 'blur(6px)',
                      color: '#ffffff',
                      border: '1px solid #334155',
                      width: '40px',
                      height: '40px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={`Delete (${selectedItems.size})`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z"/>
                    </svg>
                  </button>
                </div>
              )}

              {/* Search Row */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap' }}>
                <button
                  onClick={() => {
                    const term = prompt('Search files');
                    if (term !== null) setSearchTerm(term);
                  }}
                  title="Search"
                  style={{
                    backgroundColor: 'rgba(30, 41, 59, 0.6)',
                    backdropFilter: 'blur(6px)',
                    color: '#ffffff',
                    border: '1px solid #334155',
                    width: '40px',
                    height: '40px',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.5,14H14.71L14.43,13.73C15.41,12.59 16,11.11 16,9.5C16,5.91 13.09,3 9.5,3C5.91,3 3,5.91 3,9.5C3,13.09 5.91,16 9.5,16C11.11,16 12.59,15.41 13.73,14.43L14,14.71V15.5L19,20.49L20.49,19L15.5,14M9.5,14C7.01,14 5,11.99 5,9.5C5,7.01 7.01,5 9.5,5C11.99,5 14,7.01 14,9.5C14,11.99 11.99,14 9.5,14Z"/>
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            /* Desktop Layout (>768px) - Keep existing excellent layout */
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

              {/* Select All (desktop) - First */}
              {displayItems.length > 0 && (
                <button
                  onClick={handleSelectAll}
                  title={`Select All (${displayItems.length})`}
                  style={{
                    backgroundColor: 'rgba(30, 41, 59, 0.6)',
                    backdropFilter: 'blur(6px)',
                    color: '#ffffff',
                    border: '1px solid #334155',
                    width: '36px',
                    height: '36px',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                  <rect x="1" y="1" width="14" height="14" rx="2" fill={selectedItems.size === displayItems.length ? '#1877f2' : 'transparent'} stroke={selectedItems.size === displayItems.length ? '#1877f2' : '#4b5563'} />
                    {selectedItems.size === displayItems.length && (
                      <path d="M4 8l3 3 5-6" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    )}
                  </svg>
                </button>
              )}

              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // CRITICAL: Synchronous trigger for regular file upload
                  if (fileInputRef.current && !showExpiryModal) {
                    fileInputRef.current.removeAttribute('webkitdirectory');
                    fileInputRef.current.setAttribute('multiple', '');
                    setFolderMode(false);
                    fileInputRef.current.click();
                  }
                }}
                // Enabled at root as well
                style={{
                  backgroundColor: 'rgba(30, 41, 59, 0.6)',
                  backdropFilter: 'blur(6px)',
                  color: '#ffffff',
                  border: '1px solid #334155',
                  width: '36px',
                  height: '36px',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 1
                }}
                title="Upload Files"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M12 3v10"/>
                  <path d="M8 7l4-4 4 4"/>
                  <path d="M5 21h14a2 2 0 0 0 2-2v-3"/>
                  <path d="M3 16v3a2 2 0 0 0 2 2"/>
                </svg>
              </button>

              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // CRITICAL: Synchronous trigger - no async operations allowed
                  if (fileInputRef.current && !showExpiryModal) {
                    fileInputRef.current.setAttribute('webkitdirectory', '');
                    fileInputRef.current.removeAttribute('multiple');
                    setFolderMode(true);
                    fileInputRef.current.click();
                  }
                }}
                // Enable on root as well
                style={{
                  backgroundColor: 'rgba(30, 41, 59, 0.6)',
                  backdropFilter: 'blur(6px)',
                  color: '#ffffff',
                  border: '1px solid #334155',
                  width: '36px',
                  height: '36px',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 1
                }}
                title="Upload Folder"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M10 4H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7l-3-2z"/>
                  <path d="M12 9l4 4h-3v4h-2v-4H8l4-4z"/>
                </svg>
              </button>


              {selectedItems.size > 0 && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={handleShare}
                    style={{
                    backgroundColor: 'rgba(30, 41, 59, 0.6)',
                    backdropFilter: 'blur(6px)',
                    color: '#ffffff',
                    border: '1px solid #334155',
                    width: '36px',
                    height: '36px',
                  borderRadius: '3px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={`Share (${selectedItems.size})`}
                  >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18,16.08C17.24,16.08 16.56,16.38 16.04,16.85L8.91,12.7C8.96,12.47 9,12.24 9,12C9,11.76 8.96,11.53 8.91,11.3L15.96,7.19C16.5,7.69 17.21,8 18,8A3,3 0 0,0 21,5A3,3 0 0,0 18,2A3,3 0 0,0 15,5C15,5.24 15.04,5.47 15.09,5.7L8.04,9.81C7.5,9.31 6.79,9 6,9A3,3 0 0,0 3,12A3,3 0 0,0 6,15C6.79,15 7.5,14.69 8.04,14.19L15.16,18.34C15.11,18.55 15.08,18.77 15.08,19C15.08,20.61 16.39,21.91 18,21.91C19.61,21.91 20.92,20.61 20.92,19A2.92,2.92 0 0,0 18,16.08Z"/>
                    </svg>
                  </button>
                  <button 
                    onClick={handleBulkDownload}
                    title={`Download (${selectedItems.size})`}
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.6)',
                      backdropFilter: 'blur(6px)',
                      color: '#ffffff',
                      border: '1px solid #334155',
                      width: '36px',
                      height: '36px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 3v10.17l3.59-3.58L17 11l-5 5-5-5 1.41-1.41L11 13.17V3h1z"/>
                      <path d="M5 18h14v2H5z"/>
                    </svg>
                  </button>
                  <button 
                    onClick={() => handleDelete(Array.from(selectedItems))}
                    style={{
                  backgroundColor: 'rgba(30, 41, 59, 0.6)',
                  backdropFilter: 'blur(6px)',
                  color: '#ffffff',
                  border: '1px solid #334155',
                  width: '36px',
                  height: '36px',
                  borderRadius: '3px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={`Delete (${selectedItems.size})`}
                  >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z"/>
                    </svg>
                  </button>
                </div>
              )}

              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => {
                    const term = prompt('Search files');
                    if (term !== null) setSearchTerm(term);
                  }}
                  title="Search"
                  style={{
                    backgroundColor: 'rgba(30, 41, 59, 0.6)',
                    backdropFilter: 'blur(6px)',
                    color: '#ffffff',
                    border: '1px solid #334155',
                    width: '36px',
                    height: '36px',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.5,14H14.71L14.43,13.73C15.41,12.59 16,11.11 16,9.5C16,5.91 13.09,3 9.5,3C5.91,3 3,5.91 3,9.5C3,13.09 5.91,16 9.5,16C11.11,16 12.59,15.41 13.73,14.43L14,14.71V15.5L19,20.49L20.49,19L15.5,14M9.5,14C7.01,14 5,11.99 5,9.5C5,7.01 7.01,5 9.5,5C11.99,5 14,7.01 14,9.5C14,11.99 11.99,14 9.5,14Z"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Upload Progress */}
        {uploading && (
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '24px',
            marginBottom: '20px'
          }}>
            <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0', marginBottom: '16px' }}>
              {uploadProgress >= 100 && processingProgress < 100 
                ? `Processing files... (${processedFiles}/${totalFiles})`
                : totalFiles === 1 
                ? 'Uploading your file...' 
                : `Uploading ${totalFiles} files... (${currentFileIndex + 1}/${totalFiles})`
              }
            </h3>
            <ProgressBar 
              progress={uploadProgress >= 100 ? processingProgress : uploadProgress}
              files={files}
              currentFileIndex={currentFileIndex}
              currentFileName={currentFileName}
              totalFiles={totalFiles}
              uploadSpeed={uploadSpeed}
              bytesUploaded={bytesUploaded}
              totalBytes={totalBytes}
              destination={currentPath[currentPath.length - 1].id || "Secure Storage"}
              mode="upload"
              processingProgress={processingProgress}
              processedFiles={processedFiles}
            />
          </div>
        )}

        {/* Share Link Display - Removed for MyFilesPage */}

        {/* Items List */}
        {displayItems.length > 0 ? (
          <>
            <div ref={listContainerRef} style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
              overflow: 'visible',
              minHeight: window.innerWidth <= 768 ? '353.6px' : '313.5px'
          }}>
            {displayItems.map((item, index) => (
              <div data-row="file-row"
                key={item.id}
                style={{
                  display: 'flex',
                  flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                  alignItems: window.innerWidth <= 768 ? 'stretch' : 'center',
                  padding: window.innerWidth <= 768 ? '16px' : '12px 16px',
                  borderBottom: index < displayItems.length - 1 ? '1px solid #374151' : '0.8px solid #374151',
                  transition: 'background-color 0.2s ease',
                  cursor: 'pointer',
                  backgroundColor: selectedItems.has(item.id) ? '#374151' : 'transparent',
                  gap: window.innerWidth <= 768 ? '12px' : '0'
                }}
                onMouseEnter={(e) => {
                  if (!selectedItems.has(item.id)) {
                    e.currentTarget.style.backgroundColor = '#2a3441';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selectedItems.has(item.id)) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                onClick={() => {
                  if (item.type === 'folder') {
                    if (item.isTakenDown) {
                      setTakedownFolderInfo({
                        name: item.name,
                        reason: item.takedownReason
                      });
                      setShowTakedownModal(true);
                    } else {
                      navigateToFolder(item.id, item.name);
                    }
                  } else {
                    // Click on the row opens or closes preview instead of selecting
                    const isActive = !!activePreview && activePreview.id === item.id;
                    if (isPreviewable(item)) {
                      if (isActive) {
                        closeInlinePreview();
                      } else {
                        openInlinePreview(item);
                    }
                  } else {
                    toggleItemSelection(item.id);
                    }
                  }
                }}
              >
                {/* Mobile Layout - Compact single row inspired by the image */}
                {window.innerWidth <= 768 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Checkbox */}
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleItemSelection(item.id);
                      }}
                    >
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '1px solid #4b5563',
                        borderRadius: '2px',
                        backgroundColor: selectedItems.has(item.id) ? '#1877f2' : '#374151',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {selectedItems.has(item.id) && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Icon */}
                    <div style={{ 
                      backgroundColor: '#1a1d29', 
                      border: '1px solid #334155',
                      borderRadius: '3px', 
                      padding: '8px', 
                      color: '#6b7280'
                    }}>
                      {item.type === 'folder' ? (
                        item.isTakenDown ? (
                          // Banned folder icon
                          <div style={{ position: 'relative' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"/>
                            </svg>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ 
                              position: 'absolute', 
                              top: '-2px', 
                              right: '-2px', 
                              color: '#FF0000',
                              backgroundColor: '#1a1d29',
                              borderRadius: '50%'
                            }}>
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                              <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
                              <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          </div>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"/>
                          </svg>
                        )
                      ) : (
                        (() => { 
                          const filename = item.filename || item.originalFilename || item.name || '';
                          const iconPath = getFileTypeIcon(filename);
                          return (
                            <img 
                              src={iconPath} 
                              alt={filename}
                              style={{
                                width: '24px',
                                height: '24px',
                                objectFit: 'contain',
                                pointerEvents: 'none',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none',
                                WebkitTouchCallout: 'none',
                                WebkitUserDrag: 'none',
                                KhtmlUserSelect: 'none'
                              }}
                              draggable={false}
                              onError={(e) => {
                                // Fallback to default icon if SVG fails to load
                                e.target.src = '/icons/text_color_txt.svg';
                              }}
                            />
                          );
                        })()
                      )}
                    </div>

                    {/* Item Info - Name and details in one column */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#e2e8f0',
                        marginBottom: '2px',
                        wordBreak: 'break-word'
                      }}>
                        {item.name}
                      </div>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#9ca3af',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ whiteSpace: 'nowrap' }}>{utils.formatDate(item.createdAt)}</span>
                        {/* visibility tag removed per request */}
                      </div>
                    </div>

                    {/* Action Buttons - Clean icons on the right */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {/* QR Button (replaces eye/preview in mobile) */}
                      {showContextMenu !== item.id && (
                      <button
                          onClick={(e) => { e.stopPropagation(); openQRForItem(item); }}
                          title="Show QR"
                        style={{
                          backgroundColor: 'rgba(30, 41, 59, 0.6)',
                          backdropFilter: 'blur(6px)',
                          color: '#e2e8f0',
                          border: '1px solid #334155',
                          width: '32px',
                          height: '32px',
                          borderRadius: '3px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z"/>
                        </svg>
                      </button>
                      )}

                      {/* Rename Folder (replaces Download for folders) or Download for files */}
                      {showContextMenu !== item.id && (
                        item.type === 'folder' ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowRenameModal(item.id);
                              setRenameValue(item.name || '');
                            }}
                            style={{
                              backgroundColor: 'rgba(30, 41, 59, 0.6)',
                              backdropFilter: 'blur(6px)',
                              color: '#e2e8f0',
                              border: '1px solid #334155',
                              width: '32px',
                              height: '32px',
                              borderRadius: '3px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Rename Folder"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z"/>
                              <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.41l-2.34-2.34a1.003 1.003 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                            </svg>
                          </button>
                        ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!downloadingFiles.has(item.id || item.fileId || item.file_id)) {
                            handleDownload(item);
                          }
                        }}
                        disabled={downloadingFiles.has(item.id || item.fileId || item.file_id)}
                            title="Download"
                        style={{
                              backgroundColor: 'rgba(30, 41, 59, 0.6)',
                              backdropFilter: 'blur(6px)',
                              color: '#e2e8f0',
                              border: '1px solid #334155',
                              width: '32px',
                              height: '32px',
                              borderRadius: '3px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                              opacity: downloadingFiles.has(item.id || item.fileId || item.file_id) ? 0.6 : 1
                            }}
                          >
                            {downloadingFiles.has(item.id || item.fileId || item.file_id) ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }}>
                                <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z" opacity="0.3"/>
                                <path d="M12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8V2z"/>
                              </svg>
                            ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M12 3v10.17l3.59-3.58L17 11l-5 5-5-5 1.41-1.41L11 13.17V3h1z"/>
                              <path d="M5 18h14v2H5z"/>
                          </svg>
                            )}
                      </button>
                        )
                      )}

                      {/* Menu Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const willOpen = showContextMenu !== item.id;
                          setShowContextMenu(willOpen ? item.id : null);
                          if (willOpen) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const menuWidth = 160;
                            const left = Math.min(window.innerWidth - menuWidth - 8, Math.max(8, rect.right - menuWidth));
                            const top = Math.min(window.innerHeight - 10, rect.bottom + 8);
                            setContextMenuPos({ top, left });
                            setContextMenuItem(item);
                          } else {
                            setContextMenuItem(null);
                            setContextMenuPos(null);
                          }
                        }}
                        style={{
                          backgroundColor: 'rgba(30, 41, 59, 0.6)',
                          backdropFilter: 'blur(6px)',
                          color: '#e2e8f0',
                          border: '1px solid #334155',
                          width: '32px',
                          height: '32px',
                          borderRadius: '3px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <circle cx="12" cy="5" r="2"/>
                          <circle cx="12" cy="12" r="2"/>
                          <circle cx="12" cy="19" r="2"/>
                        </svg>
                        {/* Inline dropdown removed to prevent duplicate menu; using global portal menu below */}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Desktop Layout - Keep existing excellent layout */
                  <>
                    {/* Checkbox */}
                    <div 
                      style={{ marginRight: '12px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleItemSelection(item.id);
                      }}
                    >
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '1px solid #4b5563',
                        borderRadius: '2px',
                        backgroundColor: selectedItems.has(item.id) ? '#1877f2' : '#374151',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {selectedItems.has(item.id) && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Icon */}
                    <div style={{ 
                      backgroundColor: '#1a1d29',
                      border: '1px solid #334155',
                      borderRadius: '3px', 
                      padding: '8px', 
                      marginRight: '12px',
                      color: '#6b7280'
                    }}>
                      {item.type === 'folder' ? (
                        item.isTakenDown ? (
                          // Banned folder icon
                          <div style={{ position: 'relative' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"/>
                            </svg>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ 
                              position: 'absolute', 
                              top: '-2px', 
                              right: '-2px', 
                              color: '#FF0000',
                              backgroundColor: '#1a1d29',
                              borderRadius: '50%'
                            }}>
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                              <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
                              <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          </div>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"/>
                          </svg>
                        )
                      ) : (
                        (() => { 
                          const filename = item.filename || item.originalFilename || item.name || '';
                          const iconPath = getFileTypeIcon(filename);
                          return (
                            <img 
                              src={iconPath} 
                              alt={filename}
                              style={{
                                width: '24px',
                                height: '24px',
                                objectFit: 'contain',
                                pointerEvents: 'none',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none',
                                WebkitTouchCallout: 'none',
                                WebkitUserDrag: 'none',
                                KhtmlUserSelect: 'none'
                              }}
                              draggable={false}
                              onError={(e) => {
                                // Fallback to default icon if SVG fails to load
                                e.target.src = '/icons/text_color_txt.svg';
                              }}
                            />
                          );
                        })()
                      )}
                    </div>

                    {/* Item Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        color: '#e2e8f0',
                        marginBottom: '2px'
                      }}>
                        {item.name}
                      </div>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#9ca3af',
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                      }}>
                        <span>{utils.formatDate(item.createdAt)}</span>
                        {item.size > 0 && <span>{utils.formatFileSize(item.size)}</span>}
                        {item.expiryDate && (
                          <span style={{
                            color: new Date(item.expiryDate) < new Date(Date.now() + 24 * 60 * 60 * 1000) ? '#ef4444' : '#f59e0b',
                            fontSize: '10px',
                            fontWeight: '500',
                            backgroundColor: new Date(item.expiryDate) < new Date(Date.now() + 24 * 60 * 60 * 1000) ? '#ef444420' : '#f59e0b20',
                            padding: '2px 6px',
                            borderRadius: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.4L16.2,16.2Z"/>
                            </svg>
                            {item.type === 'folder' ? 'files expire' : 'expires'} {utils.formatDate(item.expiryDate)}
                          </span>
                        )}
                        {/* visibility toggle removed per request */}
                      </div>
                    </div>

                    {/* Action Icons (desktop) */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {/* QR code icon */}
                      {showContextMenu !== item.id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openQRForItem(item);
                          }}
                          title="Show QR"
                            style={{
                          backgroundColor: 'rgba(30, 41, 59, 0.6)',
                          backdropFilter: 'blur(6px)',
                              color: '#e2e8f0',
                          border: '1px solid #334155',
                          width: '32px',
                          height: '32px',
                          borderRadius: '3px',
                              display: 'flex',
                              alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z"/>
                          </svg>
                          </button>
                      )}

                      {/* Rename for folders; Download for files (desktop) */}
                      {showContextMenu !== item.id && (
                        item.type === 'folder' ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                              setShowRenameModal(item.id);
                              setRenameValue(item.name || '');
                        }}
                            title="Rename Folder"
                        style={{
                              backgroundColor: 'rgba(30, 41, 59, 0.6)',
                              backdropFilter: 'blur(6px)',
                              color: '#e2e8f0',
                              border: '1px solid #334155',
                              width: '32px',
                              height: '32px',
                              borderRadius: '3px',
                                display: 'flex',
                                alignItems: 'center',
                              justifyContent: 'center'
                              }}
                            >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z"/>
                              <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.41l-2.34-2.34a1.003 1.003 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                            </button>
                        ) : (
                            <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!downloadingFiles.has(item.id || item.fileId || item.file_id)) {
                                handleDownload(item);
                              }
                            }}
                            disabled={downloadingFiles.has(item.id || item.fileId || item.file_id)}
                            title="Download"
                              style={{
                              backgroundColor: 'rgba(30, 41, 59, 0.6)',
                              backdropFilter: 'blur(6px)',
                                color: '#e2e8f0',
                              border: '1px solid #334155',
                              width: '32px',
                              height: '32px',
                              borderRadius: '3px',
                                display: 'flex',
                                alignItems: 'center',
                              justifyContent: 'center',
                              opacity: downloadingFiles.has(item.id || item.fileId || item.file_id) ? 0.6 : 1
                              }}
                            >
                            {downloadingFiles.has(item.id || item.fileId || item.file_id) ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }}>
                                <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z" opacity="0.3"/>
                                <path d="M12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8V2z"/>
                              </svg>
                            ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M12 3v10.17l3.59-3.58L17 11l-5 5-5-5 1.41-1.41L11 13.17V3h1z"/>
                              <path d="M5 18h14v2H5z"/>
                              </svg>
                            )}
                            </button>
                        )
                      )}

                      {/* 3-dots menu icon */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                          const willOpen = showContextMenu !== item.id;
                          setShowContextMenu(willOpen ? item.id : null);
                          if (willOpen) {
                            const menuWidth = 160;
                            const left = Math.min(window.innerWidth - menuWidth - 8, Math.max(8, e.clientX - menuWidth + 24));
                            const top = Math.min(window.innerHeight - 10, e.clientY + 8);
                            setContextMenuPos({ top, left });
                            setContextMenuItem(item);
                          } else {
                            setContextMenuItem(null);
                            setContextMenuPos(null);
                          }
                        }}
                        title="More"
                              style={{
                          backgroundColor: 'rgba(30, 41, 59, 0.6)',
                          backdropFilter: 'blur(6px)',
                          color: '#9ca3af',
                          border: '1px solid #334155',
                          width: '32px',
                          height: '32px',
                          borderRadius: '3px',
                                display: 'flex',
                                alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <circle cx="12" cy="5" r="2"/>
                          <circle cx="12" cy="12" r="2"/>
                          <circle cx="12" cy="19" r="2"/>
                              </svg>
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
            
            // Add inline PDF viewer after the file that's being previewed
            .reduce((acc, fileElement, index) => {
              acc.push(fileElement);
              
              // Check if this file is being previewed inline
              const item = displayItems[index];
              if (activePreview && item.id === activePreview.id) {
                const mimeType = item.contentType || item.content_type || item.mime_type || '';
                const filename = item.originalFilename || item.original_filename || item.filename || '';
                const isPDF = mimeType === 'application/pdf' || filename.endsWith('.pdf');
                const isImage = mimeType.startsWith('image/') || filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/);
                
                acc.push(
                  <div key={`preview-${item.id}`} style={{ padding: '16px' }}>
                    {isPDF ? (
                      <InlinePdfViewer
                        file={activePreview}
                        folderId={currentPath[currentPath.length - 1].id}
                        onClose={closeInlinePreview}
                      />
                    ) : isImage ? (
                      <InlineImageViewer
                        file={activePreview}
                        folderId={currentPath[currentPath.length - 1].id}
                        onClose={closeInlinePreview}
                      />
                    ) : null}
                  </div>
                );
              }
              
              return acc;
            }, [])}
          </div>
          </>
        ) : (
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '48px',
            textAlign: 'center'
          }}>
            <div style={{ color: '#6b7280', marginBottom: '16px' }}>
              <svg width="48" height="48" style={{ margin: '0 auto' }} viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"/>
              </svg>
            </div>
            <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0', marginBottom: '8px' }}>
              {searchTerm ? 'No Results Found' : 'No Files Yet'}
            </h3>
            <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '24px' }}>
              {searchTerm 
                ? `No items match "${searchTerm}"`
                : currentPath[currentPath.length - 1].id === 'root'
                  ? "You haven't uploaded any files yet. Start by uploading your first file!"
                  : "This folder is empty."
              }
            </p>
            {!searchTerm && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Always trigger file upload, whether in root or subfolder
                  if (!showExpiryModal) {
                    fileInputRef.current?.click();
                  }
                }}
                style={{
                  backgroundColor: '#1877f2',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '2px',
                  fontSize: '12px',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#166fe5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#1877f2'}
              >
                Upload Files
              </button>
            )}
          </div>
        )}

        {/* Google-style Pagination */}
        {totalPages > 1 && (
          <div style={{
            padding: '20px 0',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '4px'
          }}>
            {/* Previous Button */}
            <button
              onClick={() => {
                // Batch the page change to prevent ResizeObserver issues
                requestAnimationFrame(() => {
                  setCurrentPage(Math.max(1, currentPage - 1));
                });
              }}
              disabled={currentPage === 1}
              style={{
                backgroundColor: 'transparent',
                color: currentPage === 1 ? '#6b7280' : '#9ca3af',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '2px',
                fontSize: '12px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 1) {
                  e.target.style.backgroundColor = '#374151';
                  e.target.style.color = '#e2e8f0';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 1) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#9ca3af';
                }
              }}
            >
              ‹ Previous
            </button>
            
            {/* Page Numbers */}
            {generatePageNumbers().map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span style={{
                    color: '#6b7280',
                    padding: '8px 4px',
                    fontSize: '12px'
                  }}>
                    ...
            </span>
                ) : (
                  <button
                    onClick={() => {
                      // Batch the page change to prevent ResizeObserver issues
                      requestAnimationFrame(() => {
                        setCurrentPage(page);
                      });
                    }}
                    style={{
                      backgroundColor: page === currentPage ? '#1877f2' : 'transparent',
                      color: page === currentPage ? 'white' : '#9ca3af',
                      border: page === currentPage ? '1px solid #1877f2' : '1px solid transparent',
                      padding: '8px 12px',
                      borderRadius: '2px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      minWidth: '36px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (page !== currentPage) {
                        e.target.style.backgroundColor = '#374151';
                        e.target.style.color = '#e2e8f0';
                        e.target.style.borderColor = '#374151';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page !== currentPage) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#9ca3af';
                        e.target.style.borderColor = 'transparent';
                      }
                    }}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}

            {/* Next Button */}
            <button
              onClick={() => {
                // Batch the page change to prevent ResizeObserver issues
                requestAnimationFrame(() => {
                  setCurrentPage(Math.min(totalPages, currentPage + 1));
                });
              }}
              disabled={currentPage === totalPages}
              style={{
                backgroundColor: 'transparent',
                color: currentPage === totalPages ? '#6b7280' : '#9ca3af',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '2px',
                fontSize: '12px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== totalPages) {
                  e.target.style.backgroundColor = '#374151';
                  e.target.style.color = '#e2e8f0';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== totalPages) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#9ca3af';
                }
              }}
            >
              Next ›
            </button>
          </div>
        )}

        {/* Footer Info */}
        <div style={{
          padding: '8px 0 0 0',
          textAlign: 'center',
          fontSize: '11px',
          color: '#6b7280'
        }}>
          Showing {displayItems.length} of {totalItems} items
          {selectedItems.size > 0 && (
            <span> • {selectedItems.size} selected</span>
          )}
        </div>

        {/* Google Ads Unit - Only show when viewing folders, NOT when viewing files */}
        {currentPath[currentPath.length - 1].id === 'root' && (
          <AdUnit slotType="myfiles" style={{ marginTop: '16px' }} />
        )}
      </div>

      {/* SINGLE File Input - Dynamically switches between file and folder mode */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          console.log(`📁 Input changed: ${e.target.files?.length || 0} files, folder mode: ${folderMode}`);
          
          if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files);
            console.log(`📁 Processing ${files.length} files`);
            
            // Log folder info if in folder mode
            if (folderMode && files[0].webkitRelativePath) {
              console.log('📁 Folder upload detected:', files[0].webkitRelativePath);
            }
            
            handleFileSelection(files);
            e.target.value = '';
          }
        }}
      />

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '24px',
            width: '400px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ color: '#e2e8f0', fontSize: '16px', marginBottom: '16px' }}>
              Create New Folder
            </h3>
            <input
              type="text"
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              style={{
                width: '100%',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                color: '#e2e8f0',
                padding: '8px 12px',
                borderRadius: '2px',
                fontSize: '12px',
                marginBottom: '16px'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreateFolder();
                }
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateFolder(false);
                  setNewFolderName('');
                }}
                style={{
                  backgroundColor: '#4b5563',
                  color: '#e2e8f0',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '2px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                style={{
                  backgroundColor: newFolderName.trim() ? '#1877f2' : '#4b5563',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '2px',
                  fontSize: '12px',
                  cursor: newFolderName.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {showRenameModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '24px',
            width: '400px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ color: '#e2e8f0', fontSize: '16px', marginBottom: '16px' }}>
              Rename Item
            </h3>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              style={{
                width: '100%',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                color: '#e2e8f0',
                padding: '8px 12px',
                borderRadius: '2px',
                fontSize: '12px',
                marginBottom: '16px'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleRename(showRenameModal, renameValue);
                  setShowRenameModal(null);
                  setRenameValue('');
                }
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowRenameModal(null);
                  setRenameValue('');
                }}
                style={{
                  backgroundColor: '#4b5563',
                  color: '#e2e8f0',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '2px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleRename(showRenameModal, renameValue);
                  setShowRenameModal(null);
                  setRenameValue('');
                }}
                disabled={!renameValue.trim()}
                style={{
                  backgroundColor: renameValue.trim() ? '#1877f2' : '#4b5563',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '2px',
                  fontSize: '12px',
                  cursor: renameValue.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Modal */}
      {showQRModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowQRModal(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: '#1a1d29',
            border: '1px solid #334155',
            borderRadius: '3px',
            width: '100%',
            maxWidth: '360px',
            padding: '16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#e2e8f0' }}>QR Code</h3>
              <button onClick={() => setShowQRModal(false)} style={{
                background: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer'
              }}>
                ✕
              </button>
            </div>
            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="QR Code" style={{ width: '200px', height: '200px', borderRadius: '3px', background: '#fff', display: 'block', margin: '0 auto' }} />
              ) : (
                <div style={{ color: '#9ca3af' }}>Generating...</div>
              )}
            </div>
            <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '12px', wordBreak: 'break-all', textAlign: 'center' }}>{qrTitle}</div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => {
                navigator.clipboard.writeText(qrLink).then(() => alert('Link copied to clipboard'));
              }} style={{
                backgroundColor: '#1e293b',
                color: '#e2e8f0',
                border: '1px solid #334155',
                borderRadius: '3px',
                padding: '8px 12px'
              }}>Copy Link</button>
              <button onClick={() => {
                if (!qrDataUrl) return;
                const a = document.createElement('a');
                a.href = qrDataUrl;
                a.download = `qr-${(qrTitle || 'link').replace(/[^a-z0-9]/gi,'-').toLowerCase()}.png`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }} style={{
                backgroundColor: '#1877f2',
                color: '#fff',
                border: 'none',
                borderRadius: '3px',
                padding: '8px 12px'
              }}>Download QR</button>
            </div>
          </div>
        </div>
      )}

      {/* Download Progress Modal */}
      {showDownloadProgress && (
        <>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '24px',
            width: '450px',
            maxWidth: '90vw',
            textAlign: 'center',
            position: 'relative'
          }}>
            {/* Download Icon - green theme */}
            <div style={{
              backgroundColor: '#10b98120',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              border: '1px solid #10b98140'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#10b981' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="7,10 12,15 17,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>

            <h3 style={{ 
              color: '#e2e8f0', 
              fontSize: '16px', 
              fontWeight: '500',
              marginBottom: '8px' 
            }}>
              {Object.keys(downloadProgress).length === 0
                ? 'Preparing Download...'
                : Object.keys(downloadProgress).length === 1 
                  ? 'Downloading File...' 
                  : `Downloading ${Object.keys(downloadProgress).length} Files...`
              }
            </h3>
            
            <p style={{ 
              color: '#9ca3af', 
              fontSize: '12px', 
              marginBottom: '20px',
              lineHeight: '1.4' 
            }}>
              {Object.keys(downloadProgress).length === 0
                ? 'Preparing your download, please wait...'
                : 'Your download is in progress'
              }
            </p>

            {/* Progress Bars */}
            <div style={{ textAlign: 'left' }}>
              {Object.keys(downloadProgress).length === 0 ? (
                <div style={{
                  backgroundColor: '#0b1220',
                  border: '1px solid #1f2937',
                  borderRadius: '4px',
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    border: '3px solid #10b981',
                    borderTop: '3px solid transparent',
                    borderRadius: '50%',
                    margin: '0 auto 8px',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <p style={{ color: '#a7f3d0', fontSize: '12px', margin: 0 }}>
                    Preparing download...
                  </p>
                </div>
              ) : (
                Object.values(downloadProgress).map((item, index) => (
                  <div key={item.filename} style={{ marginBottom: index < Object.values(downloadProgress).length - 1 ? '16px' : '0' }}>
                    <ProgressBar 
                      progress={item.progress}
                      files={[{ name: item.filename, size: item.total }]}
                      currentFileIndex={0}
                      currentFileName={item.filename}
                      totalFiles={1}
                      uploadSpeed={item.speed}
                      bytesUploaded={item.loaded}
                      totalBytes={item.total}
                      destination="Download"
                      mode="download"
                      remainingTime={item.remainingTime}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '20px',
            width: '350px',
            maxWidth: '90vw',
            textAlign: 'center',
            position: 'relative'
          }}>
            {/* Warning Icon */}
            <div style={{
              backgroundColor: '#fed7d7',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#f56565' }}>
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            {/* Close button */}
            <button 
              onClick={cancelDelete}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                fontSize: '16px',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>

            <h3 style={{ 
              color: '#e2e8f0', 
              fontSize: '16px', 
              fontWeight: '500',
              marginBottom: '12px' 
            }}>
              Confirm Deletion
            </h3>
            
            <p style={{ 
              color: '#9ca3af', 
              fontSize: '12px', 
              marginBottom: '20px',
              lineHeight: '1.4' 
            }}>
              You are about to delete {Array.isArray(itemsToDelete) ? itemsToDelete.length : 0} content item(s). This action cannot be undone. Are you sure you want to proceed?
            </p>
            
            <button
              onClick={confirmDelete}
              style={{
                backgroundColor: '#e53e3e',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '2px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#c53030'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#e53e3e'}
            >
              Confirm Deletion
            </button>
          </div>
        </div>
      )}

      {/* Delete Success Modal */}
      {showDeleteSuccess && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '20px',
            width: '350px',
            maxWidth: '90vw',
            textAlign: 'center',
            position: 'relative'
          }}>
            {/* Success Icon */}
            <div style={{
              backgroundColor: '#c6f6d5',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#38a169' }}>
                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            {/* Close button */}
            <button 
              onClick={() => setShowDeleteSuccess(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                fontSize: '16px',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>

            <h3 style={{ 
              color: '#e2e8f0', 
              fontSize: '16px', 
              fontWeight: '500',
              marginBottom: '12px' 
            }}>
              Success
            </h3>
            
            <p style={{ 
              color: '#9ca3af', 
              fontSize: '12px', 
              lineHeight: '1.4' 
            }}>
              {successMessage || `${deletedCount} content item(s) deleted successfully.`}
            </p>
          </div>
        </div>
      )}

      {/* Expiry Selection Modal */}
      {showExpiryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '24px',
            width: '450px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ color: '#e2e8f0', fontSize: '16px', marginBottom: '16px', fontWeight: '500' }}>
              {pendingFiles.length > 20 ? 'Upload Folder - Set Expiry' : 'Upload Files - Set Expiry'}
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                {pendingFiles.length > 20 
                  ? `Folder contents: ${pendingFiles.length} file${pendingFiles.length !== 1 ? 's' : ''}`
                  : `Files selected: ${pendingFiles.length} file${pendingFiles.length !== 1 ? 's' : ''}`
                }
              </p>
              {pendingFiles.length > 0 && (() => {
                const totalSize = pendingFiles.reduce((sum, file) => sum + (file.size || 0), 0);
                const sizeGB = (totalSize / (1024 * 1024 * 1024)).toFixed(2);
                const sizeMB = (totalSize / (1024 * 1024)).toFixed(2);
                const sizeCategory = totalSize < 5 * 1024 * 1024 * 1024 ? 'Standard' : 
                                   totalSize < 10 * 1024 * 1024 * 1024 ? 'Large' : 'Very Large';
                
                // Show MB if less than 1GB, otherwise show GB
                const displaySize = totalSize < 1024 * 1024 * 1024 ? `${sizeMB}MB` : `${sizeGB}GB`;
                
                return (
                  <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '12px' }}>
                    Total size: {displaySize} ({sizeCategory} - affects available expiry times)
                  </p>
                );
              })()}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#6b7280' }}>
                    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.4L16.2,16.2Z"/>
                  </svg>
                  <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '500' }}>
                    Auto-delete after:
                  </span>
                </div>
                <select
                  value={selectedExpiry}
                  onChange={(e) => setSelectedExpiry(e.target.value)}
                  style={{
                    backgroundColor: '#374151',
                    color: '#e2e8f0',
                    border: '1px solid #4b5563',
                    borderRadius: '2px',
                    padding: '6px 10px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  {getExpiryOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
                <p style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                  Files will be automatically deleted after expiry.
                  {(() => {
                    const opt = getExpiryOptions().find(o => o.value === selectedExpiry);
                    const label = opt?.label || '1 day';
                    return label === '1 day' ? ' Files will expire tomorrow.' : ` Files will expire in ${label}.`;
                  })()}
                </p>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowExpiryModal(false);
                  setPendingFiles([]);
                  isProcessingFiles.current = false; // Reset processing flag
                }}
                style={{
                  backgroundColor: '#4b5563',
                  color: '#e2e8f0',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '2px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowExpiryModal(false);
                  await handleFileUpload(pendingFiles, selectedExpiryDays);
                  setPendingFiles([]);
                  isProcessingFiles.current = false; // Reset processing flag
                }}
                style={{
                  backgroundColor: '#1877f2',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '2px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Upload Files
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Click outside to close context menu */}
      {showContextMenu && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        zIndex: 9999
          }}
          onClick={() => setShowContextMenu(null)}
        />
      )}

  {/* Global context menu rendered in portal to avoid clipping */}
  {showContextMenu && contextMenuItem && contextMenuPos && ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: contextMenuPos.top,
        left: contextMenuPos.left,
        backgroundColor: '#242938',
        border: '1px solid #374151',
        borderRadius: '3px',
        padding: '8px 0',
        minWidth: '160px',
        zIndex: 10001,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => {
          handleSingleShare(contextMenuItem);
          setShowContextMenu(null);
          setContextMenuItem(null);
        }}
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          border: 'none',
          color: '#22c55e',
          padding: '8px 16px',
          fontSize: '13px',
          lineHeight: '16px',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minHeight: '32px'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#22c55e20'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18,16.08C17.24,16.08 16.56,16.38 16.04,16.85L8.91,12.7C8.96,12.47 9,12.24 9,12C9,11.76 8.96,11.53 8.91,11.3L15.96,7.19C16.5,7.69 17.21,8 18,8A3,3 0 0,0 21,5A3,3 0 0,0 18,2A3,3 0 0,0 15,5C15,5.24 15.04,5.47 15.09,5.7L8.04,9.81C7.5,9.31 6.79,9 6,9A3,3 0 0,0 3,12A3,3 0 0,0 6,15C6.79,15 7.5,14.69 8.04,14.19L15.16,18.34C15.11,18.55 15.08,18.77 15.08,19C15.08,20.61 16.39,21.91 18,21.91C19.61,21.91 20.92,20.61 20.92,19A2.92,2.92 0 0,0 18,16.08Z"/></svg>
        Share
      </button>
      <button
        onClick={() => {
          setShowRenameModal(contextMenuItem.id);
          setRenameValue(contextMenuItem.name);
          setShowContextMenu(null);
          setContextMenuItem(null);
        }}
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          border: 'none',
          color: '#e2e8f0',
          padding: '8px 16px',
          fontSize: '13px',
          lineHeight: '16px',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minHeight: '32px'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"/></svg>
        Rename
      </button>
      <button
        onClick={(e) => {
          e.preventDefault();
          handleDelete([contextMenuItem.id]);
          setShowContextMenu(null);
          setContextMenuItem(null);
        }}
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          border: 'none',
          color: '#ef4444',
          padding: '8px 16px',
          fontSize: '13px',
          lineHeight: '16px',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minHeight: '32px'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ef444420'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z"/></svg>
        Delete
      </button>
    </div>,
    document.body
      )}

      {/* Takedown Modal - similar to delete confirmation */}
      {showTakedownModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#242938',
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '24px',
            width: '400px',
            maxWidth: '90vw',
            textAlign: 'center',
            position: 'relative'
          }}>
            {/* Close button */}
            <button 
              onClick={() => setShowTakedownModal(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                fontSize: '16px',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>

            {/* Warning Icon - similar to delete modal but red */}
            <div style={{
              backgroundColor: '#fed7d7',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#FF0000' }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
                <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>

            <h3 style={{ 
              color: '#e2e8f0', 
              fontSize: '16px', 
              fontWeight: '500',
              marginBottom: '16px' 
            }}>
              Folder Removed
            </h3>
            
            <p style={{ 
              color: '#9ca3af', 
              fontSize: '14px', 
              marginBottom: '16px',
              lineHeight: '1.5' 
            }}>
              The folder "{takedownFolderInfo?.name}" has been removed due to copyright infringement.
            </p>

            <div style={{
              backgroundColor: '#374151',
              border: '1px solid #4b5563',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              <div style={{ 
                color: '#f87171', 
                fontSize: '12px', 
                fontWeight: '500',
                marginBottom: '4px' 
              }}>
                Reason for Takedown:
              </div>
              <div style={{ 
                color: '#e2e8f0', 
                fontSize: '14px',
                lineHeight: '1.4'
              }}>
                {takedownFolderInfo?.reason || 'Copyright violation'}
              </div>
            </div>
            
            <button
              onClick={() => setShowTakedownModal(false)}
              style={{
                backgroundColor: '#4b5563',
                color: '#e2e8f0',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '2px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#6b7280'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#4b5563'}
            >
              OK
            </button>
          </div>
        </div>
      )}
      
      {/* CSS for download spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default MyFilesPage; 
